import { createFileRoute } from '@tanstack/react-router';
import { HubCardGrid, type HubCardCounts } from '@/components/settings/hub-card-grid';
import { useClientViewerGrantsQuery, useFirmUsersQuery } from '@/lib/api/settings';
import { useCanDeleteEngagements } from '@/lib/api/engagement-lifecycle';

/**
 * Firm settings hub (T-0110 / E10 wave 6 / README §4).
 *
 * Eight cards (users / viewers / library / queue / notifications /
 * my-notifications / ai-costs / brand) per the route table in
 * `design_handoff_caliber_v2/README.md` §4. The cards link to the
 * sub-routes; counts for the two cards this ticket actually implements
 * (users + viewers) are pulled live so the hub stays in sync with the
 * sub-screens.
 */

export const Route = createFileRoute('/settings/')({
  component: SettingsHub,
});

function SettingsHub() {
  const usersQuery = useFirmUsersQuery();
  const viewersQuery = useClientViewerGrantsQuery();
  const canDeleteEngagements = useCanDeleteEngagements();

  const counts: HubCardCounts = {
    ...(usersQuery.data
      ? {
          usersTotal: usersQuery.data.length,
          usersActive: usersQuery.data.filter((u) => u.status === 'active').length,
        }
      : {}),
    ...(viewersQuery.data
      ? {
          viewersActive: viewersQuery.data.items.filter((v) => v.status === 'active').length,
          viewersPending: viewersQuery.data.items.filter((v) => v.status === 'invited').length,
          viewersExpired: viewersQuery.data.items.filter((v) => v.status === 'revoked').length,
        }
      : {}),
  };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-1">
        <h1 className="text-h1 font-semibold text-ink-900">Firm settings</h1>
        <p className="text-meta text-ink-500">
          Administrator surface — provision users, manage external client viewers, govern AI spend,
          configure brand &amp; methodology.
        </p>
      </div>
      <HubCardGrid counts={counts} canDeleteEngagements={canDeleteEngagements} />
    </div>
  );
}
